import React, { useState, useRef, useCallback } from 'react';
import type { FeedbackType } from '../types';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface FeedbackModalProps {
  conversationId: string;
  messageSequence: number;
  onClose: () => void;
}

type RecordingState = 'idle' | 'recording' | 'recorded';

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  conversationId,
  messageSequence,
  onClose,
}) => {
  const { user } = useAuth();
  const [feedbackType, setFeedbackType] = useState<FeedbackType | null>(null);
  const [textContent, setTextContent] = useState('');
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
      });

      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setRecordingState('recorded');

        // Stop all tracks
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(100); // Collect data every 100ms
      setRecordingState('recording');
    } catch (err) {
      console.error('Failed to start recording:', err);
      setError('Could not access microphone. Please check permissions.');
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && recordingState === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }, [recordingState]);

  const clearRecording = useCallback(() => {
    setAudioBlob(null);
    setRecordingState('idle');
  }, []);

  const handleSubmit = async () => {
    if (!feedbackType) {
      setError('Please select a feedback type');
      return;
    }

    if (!textContent.trim() && !audioBlob) {
      setError('Please provide feedback via text or audio');
      return;
    }

    if (!user) {
      setError('You must be logged in to submit feedback');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await apiService.submitFeedback({
        conversationId,
        messageSequence,
        feedbackType,
        content: textContent.trim() || undefined,
        audio: audioBlob || undefined,
      });

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Close modal on escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-[4px] shadow-xl p-6 max-w-md w-full mx-4 text-center">
          <div className="text-green-500 text-4xl mb-4">✓</div>
          <h3 className="text-lg font-semibold text-gray-900">Thank you!</h3>
          <p className="text-gray-600 mt-2">Your feedback has been submitted.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-[4px] shadow-xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">What went wrong?</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* Auth warning */}
          {!user && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-[4px] p-3 text-sm text-yellow-800">
              You must be logged in to submit feedback.
            </div>
          )}

          {/* Type selection */}
          <div className="flex gap-3">
            <button
              onClick={() => setFeedbackType('factual_error')}
              className={`flex-1 p-3 rounded-[4px] border-2 transition-all ${
                feedbackType === 'factual_error'
                  ? 'border-[#fec35c] bg-red-50 text-[#fec35c]'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              <div className="text-2xl mb-1">📊</div>
              <div className="font-medium text-sm">Factual Error</div>
              <div className="text-xs text-gray-500 mt-1">Data was incorrect</div>
            </button>

            <button
              onClick={() => setFeedbackType('behavior_issue')}
              className={`flex-1 p-3 rounded-[4px] border-2 transition-all ${
                feedbackType === 'behavior_issue'
                  ? 'border-[#fec35c] bg-red-50 text-[#fec35c]'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              <div className="text-2xl mb-1">🤖</div>
              <div className="font-medium text-sm">Behavior Issue</div>
              <div className="text-xs text-gray-500 mt-1">Response wasn't helpful</div>
            </button>
          </div>

          {/* Text input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Describe the issue:
            </label>
            <textarea
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder="What was wrong and what should the response have been?"
              className="w-full h-24 px-3 py-2 border border-gray-300 rounded-[4px] focus:outline-none focus:ring-2 focus:ring-[#fec35c] focus:border-transparent resize-none text-sm"
            />
          </div>

          {/* Audio recording */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Or record audio:
            </label>
            <div className="flex items-center gap-3">
              {recordingState === 'idle' && (
                <button
                  onClick={startRecording}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-[4px] text-gray-700 transition-colors"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                  </svg>
                  Record
                </button>
              )}

              {recordingState === 'recording' && (
                <button
                  onClick={stopRecording}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 rounded-[4px] text-white transition-colors animate-pulse"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="6" width="12" height="12" rx="2" />
                  </svg>
                  Stop Recording
                </button>
              )}

              {recordingState === 'recorded' && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-green-600 flex items-center gap-1">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Audio recorded
                  </span>
                  <button
                    onClick={clearRecording}
                    className="text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-[4px] p-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !feedbackType || (!textContent.trim() && !audioBlob) || !user}
            className="px-4 py-2 bg-[#fec35c] hover:bg-[#d04d37] text-white rounded-[4px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Submitting...
              </>
            ) : (
              'Submit'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
